import React, { FC, useCallback, useState } from 'react';
import { tokens } from '@beeline.com/bp-design-system';
import styled from 'styled-components';
import ArrowUpIcon from 'common/Icons/ArrowUpIcon';
import { useQuickStartIntl } from '@oc/pages/QuickStart/translations/useQuickStartIntl';

export interface DropBoxProps {
  selectFileMessage: string;
  onAddFile?: (file: File) => void;
}

export const DropBox: FC<React.PropsWithChildren<DropBoxProps>> = ({ selectFileMessage, onAddFile }) => {
  const [dragging, setDragging] = useState<boolean>(false);
  const intl = useQuickStartIntl();

  const handleDropCapture = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setDragging(false);
      const file = event?.dataTransfer?.files?.[0];
      if (file && onAddFile) {
        onAddFile(file);
      }
    },
    [onAddFile]
  );

  const handleAddFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file && onAddFile) {
      onAddFile(file);
    }
  };

  const handleMouseLeave = useCallback(() => {
    setDragging(false);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event?.preventDefault?.();
    setDragging(true);
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'move';
    return false;
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    setDragging(true);
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    setDragging(true);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  return (
    <StyledDropBox
      $isDragging={dragging}
      onDropCapture={handleDropCapture}
      onMouseLeave={handleMouseLeave}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragStart={handleDragStart}
    >
      <ArrowUpIcon width={100} />
      <StyledDropCopy>
        {intl.dragAndDropOr}
        <StyledSelectFileSpan>
          {selectFileMessage}{' '}
          <StyledInput formEncType="multipart/form-data" type="file" accept=".csv" onChange={handleAddFile} />
        </StyledSelectFileSpan>{' '}
        {intl.toUpload}
      </StyledDropCopy>
      {dragging && <StyledBlockingScrim />}
    </StyledDropBox>
  );
};

export default DropBox;

const StyledBlockingScrim = styled.div`
  opacity: 0.5;
  background-color: black;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
  z-index: 1;
`;

const StyledInput = styled.input`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`;

const StyledDropCopy = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
  padding-bottom: 8px;
`;
const StyledSelectFileSpan = styled.label`
  color: ${({ theme }) => theme.colorPrimary};
  cursor: pointer;
  &:hover {
    color: #3086de;
  }
`;

const StyledDropBox = styled.div<{ $isDragging: boolean }>`
  min-height: 250px;
  margin: 1.5% 0px;
  z-index: 3;
  background-color: ${tokens.colorSurface};
  width: 100%;
  border: ${({ $isDragging }) => ($isDragging ? `2px` : `1px`)} dashed
    ${({ $isDragging }) => ($isDragging ? tokens.colorPrimary : tokens.colorOutline)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ $isDragging }) => ($isDragging ? tokens.shadowModerate : `none`)};
`;
